import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AnnouncementBar from '@/components/AnnouncementBar';
import Home from '@/pages/Home';
import Auth from '@/pages/Auth';
import Browse from '@/pages/Browse';
import ListingDetail from '@/pages/ListingDetail';
import SellerProfile from '@/pages/SellerProfile';
import SellerDashboard from '@/pages/SellerDashboard';
import AdminPanel from '@/pages/AdminPanel';
import Donation from '@/pages/Donation';
import EmailVerify from '@/pages/EmailVerify';
import TOS from '@/pages/TOS';
import Feedback from '@/pages/Feedback';
import ResetPassword from '@/pages/ResetPassword';
import Rules from '@/pages/Rules';
import LinksAndFAQ from '@/pages/LinksAndFAQ';
import AccountSettings from '@/pages/AccountSettings';
import '@/App.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-rm-bg flex items-center justify-center"><div className="w-8 h-8 border-2 border-rm-red border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <div className="min-h-screen bg-rm-bg flex flex-col">
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/listings/:id" element={<ListingDetail />} />
          <Route path="/sellers/:id" element={<SellerProfile />} />
          <Route path="/verify-email" element={<EmailVerify />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/donate" element={<Donation />} />
          <Route path="/tos" element={<TOS />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/links" element={<LinksAndFAQ />} />
          <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute roles={['seller', 'admin']}><SellerDashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminPanel /></ProtectedRoute>} />
          {/* Username-based seller profile — must be LAST */}
          <Route path="/:username" element={<SellerProfile />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || 'not-configured'}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}
